import { useState, useEffect, useCallback } from 'react';
import '../../Styles/ArtistRoot.css';

import { Link, useNavigate } from "react-router-dom";
import { MdAlbum, MdAdd, MdClose, MdLibraryAdd } from 'react-icons/md';
import { Autocomplete, TextField } from '@mui/material';
import izenuAPI from '../../services/izenuAPI';
import GENRES from '../../utils/Genres';
import UpcQuestion, { upcProblem, upcPayload } from './UpcQuestion';
import '../../Styles/Admin.css';

const TYPE_LABELS = { album: 'Álbum', ep: 'EP', single: 'Single' };
const KIND = { album: 'álbum', ep: 'EP', single: 'single' };
const ARTWORK_MAX_MB = 10;

export default function Releases() {
    const navigate = useNavigate();
    const [releases, setReleases] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [saleFilter, setSaleFilter] = useState('all'); // 'all' | 'public' | 'forsale'
    const [showCreate, setShowCreate] = useState(false);

    // Create form state
    const [artists, setArtists] = useState([]);
    const [selectedArtist, setSelectedArtist] = useState(null);
    const [title, setTitle] = useState('');
    const [type, setType] = useState('album');
    const [genre, setGenre] = useState('');
    const [upcMode, setUpcMode] = useState('');
    const [upc, setUpc] = useState('');
    const [showUpcError, setShowUpcError] = useState(false);
    const [artworkFile, setArtworkFile] = useState(null);
    const [artworkPreview, setArtworkPreview] = useState(null);
    const [creating, setCreating] = useState(false);
    const [artworkError, setArtworkError] = useState('');
    const [loaded, setLoaded] = useState(false);

    const distributorId = localStorage.getItem('id');

    const listReleases = useCallback(async () => {
        try {
            const { data: r } = await izenuAPI.get('/distributor/releases/listReleases.php');
            if (r.response) {
                setReleases(r.releases || []);
            }
        } catch (err) {
            console.log(err);
        }
        setLoaded(true);
    }, []);

    const fetchArtists = useCallback(async () => {
        try {
            const { data: r } = await izenuAPI.get('/distributor/artist/fetchArtists.php?distributor_id=' + distributorId);
            if (r.artists) {
                setArtists(r.artists);
            }
        } catch (err) {
            console.log(err);
        }
    }, [distributorId]);

    const handleCreate = useCallback(async () => {
        if (!selectedArtist || !title.trim() || !genre) {
            alert('Seleciona um artista, preenche o título e escolhe o género.');
            return;
        }
        if (upcProblem(upcMode, upc)) {
            setShowUpcError(true);
            return;
        }
        setCreating(true);
        try {
            const { data: r } = await izenuAPI.post('/distributor/releases/createRelease.php', {
                artist_id: selectedArtist.user_id,
                title: title.trim(),
                type,
                genre,
                ...upcPayload(upcMode, upc)
            });
            if (r.response && r.release_id) {
                // Upload artwork if selected
                if (artworkFile) {
                    try {
                        const formData = new FormData();
                        formData.append('artwork', artworkFile);
                        formData.append('release_id', r.release_id);
                        formData.append('artist_id', selectedArtist.user_id);
                        await izenuAPI.post('/distributor/releases/uploadArtwork.php', formData);
                    } catch (artErr) {
                        console.log('Artwork upload failed:', artErr);
                        // The release exists already; say so instead of leaving it silently coverless.
                        alert(`O ${KIND[type] || 'álbum'} foi criado, mas não foi possível enviar a capa. Podes enviá-la na página dele.`);
                    }
                }
                // Reset form
                setShowCreate(false);
                setTitle('');
                setType('album');
                setGenre('');
                setUpcMode('');
                setUpc('');
                setShowUpcError(false);
                setSelectedArtist(null);
                setArtworkFile(null);
                setArtworkPreview(null);
                // Navigate to the new release
                navigate('/release/' + r.release_id);
            } else {
                alert('Erro: ' + (r.message || 'Falha ao criar'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao criar.');
        }
        setCreating(false);
    }, [selectedArtist, title, type, genre, upcMode, upc, artworkFile, navigate]);

    const handleArtworkChange = useCallback((e) => {
        const file = e.target.files[0];
        setArtworkError('');
        if (!file) return;
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
            setArtworkError('Formato não suportado. Usa JPG, PNG ou WEBP.');
            return;
        }
        if (file.size > ARTWORK_MAX_MB * 1024 * 1024) {
            setArtworkError(`A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${ARTWORK_MAX_MB} MB.`);
            return;
        }
        setArtworkFile(file);
        setArtworkPreview(URL.createObjectURL(file));
    }, []);

    const coverUrl = (artwork) => {
        if (!artwork) return null;
        const ext = (artwork.split('/').pop().split('.').pop() || '').toLowerCase();
        return ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? artwork : null;
    };

    const counts = {
        all: releases.length,
        public: releases.filter(e => !e.for_sale).length,
        forsale: releases.filter(e => e.for_sale).length,
        singles: releases.filter(e => e.type === 'single').length,
    };

    const filteredReleases = releases.filter((e) => {
        if (saleFilter === 'forsale' && !e.for_sale) return false;
        if (saleFilter === 'singles' && e.type !== 'single') return false;
        if (saleFilter === 'public' && e.for_sale) return false;
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return (e.title || '').toLowerCase().includes(term) || (e.artist_name || '').toLowerCase().includes(term);
    });

    useEffect(() => {
        listReleases();
    }, [listReleases]);

    useEffect(() => () => { if (artworkPreview) URL.revokeObjectURL(artworkPreview); }, [artworkPreview]);

    const openCreate = () => {
        setShowCreate(v => !v);
        if (artists.length === 0) fetchArtists();
    };

    const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';

    return (
        <div className="mx-auto max-w-7xl p-3 sm:p-6 text-left">
            {/* Header */}
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="min-w-0 lg:flex-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Álbuns / EPs</h1>
                    <p className="text-sm text-gray-500">{counts.all} no total · {counts.forsale} à venda</p>
                </div>
                {/* Phone: primary action on top, both full width. */}
                <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                    <button type="button" onClick={openCreate}
                        className="flex items-center justify-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium !text-white hover:bg-amber-600 sm:order-last">
                        {showCreate ? <MdClose size={18} /> : <MdAdd size={18} />} {showCreate ? 'Fechar' : 'Criar Álbum / EP'}
                    </button>
                    <Link to="/merge-singles"
                        className="flex items-center justify-center gap-1.5 rounded-lg !border !border-solid border-gray-300 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        <MdLibraryAdd size={18} /> Agrupar singles num álbum
                    </Link>
                </div>
            </div>

            {/* Create release form */}
            {showCreate && (
                <section className="mb-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                    <h2 className="mb-2 text-lg font-semibold text-gray-900">Novo Álbum / EP</h2>
                    <p className="mb-4 text-sm text-gray-500">
                        Já tens as músicas carregadas como singles?{' '}
                        <Link to="/merge-singles" className="font-medium text-amber-700 hover:underline">Agrupa-as num álbum</Link>{' '}
                        em vez de criares um vazio e o encheres faixa a faixa.
                    </p>
                    <div className="flex flex-col gap-5 sm:flex-row">
                        <div className="flex shrink-0 flex-col items-center sm:w-48">
                            <label className="relative flex h-40 w-40 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:border-amber-400">
                                {artworkPreview
                                    ? <img src={artworkPreview} className="h-full w-full object-cover" alt="" />
                                    : <span className="flex flex-col items-center gap-1 text-sm text-gray-500"><MdAlbum size={40} className="text-gray-400" />Escolher capa</span>}
                                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleArtworkChange} className="sr-only" />
                            </label>
                            <ul className="mt-2 w-full space-y-0.5 text-xs [&_li]:!p-0 [&_li]:!text-gray-500">
                                <li><strong className="font-medium text-gray-700">Formato:</strong> JPG, PNG ou WEBP</li>
                                <li><strong className="font-medium text-gray-700">Tamanho ideal:</strong> quadrada, 3000 × 3000 px</li>
                                <li><strong className="font-medium text-gray-700">Peso máximo:</strong> {ARTWORK_MAX_MB} MB</li>
                            </ul>
                            {artworkError && <p className="mt-1 w-full text-xs text-red-600">{artworkError}</p>}
                        </div>

                        <div className="grid min-w-0 flex-1 grid-cols-1 content-start gap-3 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <Autocomplete
                                    options={artists}
                                    getOptionLabel={(o) => o.artist_name || o.name || ''}
                                    value={selectedArtist}
                                    onChange={(e, v) => setSelectedArtist(v)}
                                    renderInput={(params) => <TextField {...params} label="Artista" size="small" />}
                                    size="small"
                                />
                            </div>
                            <label className="block sm:col-span-2">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Título *</span>
                                <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Tipo</span>
                                <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
                                    <option value="album">Álbum</option>
                                    <option value="ep">EP</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Género *</span>
                                <select className={inputCls} value={genre} onChange={(e) => setGenre(e.target.value)} required>
                                    <option value="" disabled>Escolhe o género</option>
                                    {GENRES.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                                </select>
                            </label>
                            <div className="sm:col-span-2">
                                <UpcQuestion mode={upcMode} upc={upc} onModeChange={setUpcMode} onUpcChange={setUpc} showError={showUpcError} />
                            </div>
                            <div className="flex flex-col-reverse gap-2 pt-2 sm:col-span-2 sm:flex-row sm:justify-end">
                                <button type="button" onClick={() => setShowCreate(false)}
                                    className="rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
                                <button type="button" onClick={handleCreate} disabled={creating || !selectedArtist || !title.trim() || !genre}
                                    className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">
                                    {creating ? 'A criar…' : 'Criar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Filters */}
            <div className="mb-5 flex flex-wrap items-center gap-2">
                {[
                    { key: 'all', label: 'Todos' },
                    { key: 'public', label: 'Públicos' },
                    { key: 'forsale', label: 'À venda' },
                    { key: 'singles', label: 'Singles' },
                ].map(opt => (
                    <button key={opt.key} type="button" onClick={() => setSaleFilter(opt.key)}
                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition !border !border-solid ${saleFilter === opt.key ? 'border-amber-500 bg-amber-500 !text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {opt.label} <span className="opacity-70">{counts[opt.key]}</span>
                    </button>
                ))}
                <div className="search-distributors-input ms-auto w-full sm:w-auto sm:min-w-[280px]">
                    <input type="search" placeholder="Pesquisar por título ou artista…" value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '8px 0', fontSize: 14 }} />
                </div>
            </div>

            {/* Grid */}
            {!loaded ? (
                <p className="py-16 text-center text-sm text-gray-500">A carregar…</p>
            ) : releases.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">Ainda não há álbuns nem singles. Cria o primeiro com "Criar Álbum / EP".</p>
            ) : filteredReleases.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">Nada encontrado{searchTerm ? ` para "${searchTerm}"` : ''}.</p>
            ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5">
                    {filteredReleases.map((e) => {
                        const cover = coverUrl(e.artwork);
                        const tracks = Number(e.track_count) || 0;
                        return (
                            <Link key={e.id} to={`/release/${e.id}`} className="group overflow-hidden rounded-xl border border-gray-200 bg-white transition hover:border-amber-400 hover:shadow-md">
                                <div className="relative aspect-square bg-gray-200">
                                    {cover
                                        ? <img src={cover} alt="" loading="lazy" className="h-full w-full object-cover" />
                                        : <div className="flex h-full w-full items-center justify-center"><MdAlbum size={48} className="text-gray-400" /></div>}
                                    <span className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide !text-white ${e.for_sale ? 'bg-amber-500' : 'bg-green-600'}`}>
                                        {e.for_sale ? 'À venda' : 'Público'}
                                    </span>
                                </div>
                                <div className="p-2.5 sm:p-3">
                                    <p className="truncate text-sm font-semibold text-gray-900" title={e.title}>{e.title}</p>
                                    <p className="truncate text-sm text-gray-600">{e.artist_name}</p>
                                    <p className="mt-0.5 text-xs text-gray-400">{TYPE_LABELS[e.type] || e.type} · {tracks} {tracks === 1 ? 'faixa' : 'faixas'}</p>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
